// configurator-engine.js
// Headless Wizard session 3 (2026-10-22, spec claude/headless-wizard-spec-v1.md
// section 6, R-HW-14, G1 report section 1.1). Engine data layer for the
// Quantify configurator: fetches the wizard-options endpoint and renders the
// SAME DOM data layer the Liquid snippet renders today, then mounts the
// server-configurator-quantify element, so configurator-quantify.js (which
// reads the DOM only) runs unchanged: selectedDrives, trayMap, pre-quote
// pricing, the config string and the bundle submit are untouched.
//
// Mount order: snippets/server-configurator-engine.liquid renders a mount
// placeholder (.q-engine-mount, fetch parameters as data attributes) and the
// full shell markup inside an inert <template id="q-engine-shell"> (custom
// elements inside a template never upgrade). This script fills every
// option slot inside the template content, then swaps the placeholder for
// the template's serialized HTML. The parser-created custom element is
// upgraded after the whole subtree exists, which is when init() runs.
//
// Keys: radio values are skus (memory "{sku}-{count}") instead of Shopify
// product ids (R-HW-14); drive group tokens are option group codes (the JS
// treats data-group-gid and the drive_group_limits tokens as opaque keys).
// Prices arrive in cents (R-HW-15). Consumption, core counts and PSU max
// watts come from the registry columns (R-HW-7).
//
// Failure posture: any non-200 response (404 unknown store or key, 503 read
// failure), a thrown fetch, or a malformed tree renders the red
// "Configurator unavailable" message in place of the configurator; never a
// partial wizard.
//
// Config echo (R-HW-14): the page's ?config= query string is passed to the
// endpoint and read back from meta.config. After the mount, the string is
// parsed with the G1 section 5 rules and applied through the element's own
// public methods (setRadioValue, applyDriveQty, applyEmptyTrayQty, recalc),
// so the Popular snapshot keeps the curated defaults and prefill reads as a
// manual change. Prefill is best effort, not a gate: unknown skus are
// skipped.
//
// Copy config: configurator-copyconfig.js looks the element up once when the
// custom element is defined, which is before the fetch resolves here, so the
// section omits that script tag in engine mode and this script injects it
// after the mount (data-q-copyconfig-src).
//
// Still Liquid in both modes this session: the Select Server Configuration
// dropdown (D-S3-1 default), the *_modal guides, the network card notice,
// template_name and the legacy template GID. No em dashes.
(function () {
  'use strict';
  var TAG = 'server-configurator-quantify';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // Liquid handleize: lowercase, non-alphanumeric runs to '-', trimmed.
  function handleize(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function num(v, fallback) {
    var n = Number(v);
    return Number.isFinite(n) ? n : (fallback === undefined ? 0 : fallback);
  }
  function uniq(arr) {
    var out = [];
    arr.forEach(function (v) { if (v != null && v !== '' && out.indexOf(v) === -1) out.push(v); });
    return out;
  }
  function gbLabel(gb) {
    gb = num(gb);
    if (gb >= 1024 && gb % 1024 === 0) return (gb / 1024) + 'TB';
    return gb + 'GB';
  }
  function icon(name) {
    var t = document.querySelector('.q-engine-mount template[data-q-icon="' + name + '"]');
    return t ? t.innerHTML : '';
  }

  // ---------- filter row (snippets/configurator-filter.liquid) ----------

  function filterHtml(groups) {
    // groups: [{field, label, values: [raw labels], active: raw label}]
    var h = '<div class="configurator__filters">';
    groups.forEach(function (g) {
      h += '<div class="configurator__filter-group" data-filter-field="' + esc(g.field) + '">';
      if (g.label) h += '<span class="configurator__filter-label configurator__label">' + esc(g.label) + '</span>';
      h += '<div class="configurator__filter-buttons">';
      g.values.forEach(function (v) {
        var on = g.active != null && v === g.active;
        h += '<button class="configurator__filter-btn ' + (on ? 'configurator__filter-btn--active' : '') + '"' +
          ' data-filter-field="' + esc(g.field) + '"' +
          ' data-filter-value="' + esc(handleize(v)) + '"' +
          (on ? ' data-is-default="true"' : '') + '>' +
          '<span class="configurator__filter-btn-title">' + esc(String(v).trim()) + '</span></button>';
      });
      h += '</div></div>';
    });
    return h + '</div>';
  }

  // ---------- radio card (processor, heatsink, memory, raid, remote, psu, nic, add-on) ----------

  function radioCard(o) {
    // o: {name, value, checked, qsku, unitCents, qty, consumption?, maxWatts?, title,
    //     liAttrs?, inputAttrs?, liClass?, included?, description?}
    var liClass = 'configurator__card' + (o.liClass ? ' ' + o.liClass : '') + (o.checked ? ' configurator__card--selected' : '');
    var h = '<li class="' + liClass + '"' + (o.liAttrs || '') + '>' +
      '<label class="configurator__card-label">' +
      '<input type="radio" name="' + esc(o.name) + '" value="' + esc(o.value) + '"' + (o.checked ? ' checked' : '') +
      ' data-qsku="' + esc(o.qsku == null ? '' : o.qsku) + '"' +
      ' data-unit-cents="' + num(o.unitCents) + '"' +
      ' data-qty="' + num(o.qty) + '"' +
      (o.consumption !== undefined ? ' data-consumption="' + num(o.consumption) + '"' : '') +
      (o.maxWatts !== undefined ? ' data-max-watts="' + num(o.maxWatts) + '"' : '') +
      (o.inputAttrs || '') +
      ' class="configurator__selector" hidden>' +
      '<div class="configurator__card-content">' +
      '<div class="configurator__card-info"><h3 class="configurator__card-title">' + esc(o.title) + '</h3>' +
      (o.description ? '<p class="configurator__card-description">' + esc(o.description) + '</p>' : '') + '</div>' +
      '<div class="configurator__card-selection">' +
      '<p class="configurator__card-price q-price" data-line-cents="' + (num(o.unitCents) * num(o.qty)) + '"' +
      (o.included ? ' data-included="true">Included' : '>') + '</p>' +
      '<div class="configurator__custom-radio"></div></div></div></label></li>';
    return h;
  }

  // ---------- drive card (stepper card, Recommended and group lists) ----------

  function driveCard(o, maxDrives, meta) {
    return '<li class="configurator__card"><label class="configurator__card-label">' +
      '<div class="configurator__card-content">' +
      '<div class="configurator__card__header-wrapper">' +
      '<div class="configurator__card-header">' +
      '<h3 class="configurator__card-title">' + esc(o.title) + '</h3>' +
      '<p class="configurator__card-price q-price q-drive-price" data-unit-cents="' + num(o.price) + '" data-consumption="' + num(o.consumption_watts) + '"></p>' +
      '</div>' +
      '<span class="configurator__card-meta">' + esc(meta) + '</span>' +
      '</div>' +
      '<div class="configurator__card-actions"><div class="quantity">' +
      '<button class="quantity__button" name="minus" type="button">' + icon('minus') + '</button>' +
      '<input class="quantity__input" type="number" min="0" max="' + num(maxDrives) + '" value="0"' +
      ' data-drive-qsku="' + esc(o.sku) + '"' +
      ' data-drive-ff="' + esc(o.drive_ff || '2.5') + '"' +
      ' data-unit-cents="' + num(o.price) + '"' +
      ' data-title="' + esc(o.title) + '">' +
      '<button class="quantity__button" name="plus" type="button">' + icon('plus') + '</button>' +
      '</div></div></div></label></li>';
  }

  function infoNotice(text) {
    return '<div class="configurator__info-notice"><span class="configurator__info-icon">' + icon('info') + '</span><p>' + esc(text) + '</p></div>';
  }

  // ---------- tree helpers ----------

  function group(tree, key) {
    var g = null;
    (tree.groups || []).forEach(function (x) { if (x && x.key === key) g = x; });
    return g;
  }
  function defaultOf(opts) {
    var d = null;
    (opts || []).forEach(function (o) { if (o && o.default && !d) d = o; });
    return d;
  }

  // ---------- builders ----------

  function buildProcessors(tpl, tree) {
    var g = group(tree, 'processors') || { partitions: [], qty: 1 };
    var qty = num(g.qty, 1) || 1;
    var parts = g.partitions || [];
    var series = [], cats = [], dSeries = null, dCat = null, cards = '';
    parts.forEach(function (p) {
      series.push(p.series_name || p.series || p.key);
      cats.push(p.category_name || '');
      var sHandle = handleize(p.series_name || p.series || p.key);
      var cHandle = handleize(p.category_name || '');
      (p.options || []).forEach(function (o) {
        var cores = num(o.core_count);
        var title = qty + 'x ' + (o.title || o.sku) + (cores > 0 ? ' (' + (cores * qty) + ' Cores Total)' : '');
        if (o.default) { dSeries = p.series_name || p.series || p.key; dCat = p.category_name || ''; }
        var fAttrs = ' data-filter_processor_serie="' + esc(sHandle) + '" data-filter_processor_category="' + esc(cHandle) + '"';
        cards += radioCard({
          name: 'processor-selection', value: o.sku, checked: !!o.default, qsku: o.sku,
          unitCents: o.price, qty: qty, consumption: o.consumption_watts, title: title,
          liAttrs: fAttrs, inputAttrs: fAttrs
        });
      });
    });
    series = uniq(series); cats = uniq(cats);
    if (dSeries == null && parts.length) { dSeries = parts[0].series_name || parts[0].series || parts[0].key; dCat = parts[0].category_name || ''; }
    var slot = tpl.querySelector('[data-q-slot="proc-filter"]');
    if (slot) slot.outerHTML = filterHtml([
      { field: 'filter_processor_serie', label: 'Series', values: series, active: dSeries },
      { field: 'filter_processor_category', label: 'Category', values: cats, active: dCat }
    ]);
    var label = tpl.querySelector('[data-q-slot="proc-label"]');
    if (label) { label.textContent = 'CPU (' + qty + 'x included in build)'; label.removeAttribute('data-q-slot'); }
    tpl.querySelector('#configurator-processors').innerHTML = cards;
    return qty;
  }

  function buildHeatsinks(tpl, tree) {
    var g = group(tree, 'heatsinks') || { options: [], qty: 1 };
    var qty = num(g.qty, 1) || 1;
    var cards = '';
    (g.options || []).forEach(function (o) {
      cards += radioCard({
        name: 'heatsink-selection', value: o.sku, checked: false, qsku: o.sku,
        unitCents: o.price, qty: qty, consumption: o.consumption_watts,
        title: (qty > 1 ? qty + 'x ' : '') + (o.title || o.sku)
      });
    });
    tpl.querySelector('#configurator-heatsinks').insertAdjacentHTML('beforeend', cards);
    return qty;
  }

  function buildMemory(tpl, tree, sockets) {
    var g = group(tree, 'memory') || { partitions: [], counts: [] };
    var dimm = num(g.dimm_slots, num(tree.chassis && tree.chassis.dimm_slots));
    var step = num(g.memory_step, num(tree.chassis && tree.chassis.memory_step)) || sockets || 1;
    var ladder = Array.isArray(g.memory_counts) ? g.memory_counts : [];
    var counts = Array.isArray(g.counts) ? g.counts : [];
    var dSku = g.default_sku || null;
    var dCount = g.default_count == null ? null : num(g.default_count);
    var speeds = [], dSpeed = null, cards = '';
    (g.partitions || []).forEach(function (p) {
      var speedLabel = p.label || p.speed || p.key;
      speeds.push(speedLabel);
      var sHandle = handleize(speedLabel);
      (p.options || []).forEach(function (o) {
        var moduleGb = num(o.module_gb);
        var sizeLabel = gbLabel(moduleGb);
        if (dSku && o.sku === dSku) dSpeed = speedLabel;
        counts.forEach(function (c) {
          c = num(c);
          if (c < 1) return;
          var totalGb = moduleGb * c;
          var isDefault = !!(dSku && o.sku === dSku && dCount !== null && c === dCount);
          cards += radioCard({
            name: 'memory-selection', value: o.sku + '-' + c, checked: isDefault, qsku: o.sku,
            unitCents: o.price, qty: c, consumption: o.consumption_watts,
            title: gbLabel(totalGb) + ' ' + speedLabel + ' (' + c + 'x ' + sizeLabel + ')',
            liClass: 'q-memory-card',
            liAttrs: ' data-filter_memory_speed="' + esc(sHandle) + '" data-total-gb="' + totalGb + '"',
            inputAttrs: ' data-filter_memory_speed="' + esc(sHandle) + '"'
          });
        });
      });
    });
    var sub = tpl.querySelector('[data-q-slot="mem-sub"]');
    if (sub) {
      sub.textContent = ladder.length
        ? dimm + ' DIMM slots, available configurations: ' + ladder.join(', ')
        : dimm + ' DIMM slots, modules added in sets of ' + step;
      sub.removeAttribute('data-q-slot');
    }
    var slot = tpl.querySelector('[data-q-slot="mem-filter"]');
    if (slot) slot.outerHTML = filterHtml([{ field: 'filter_memory_speed', label: 'RAM Clock Speed', values: uniq(speeds), active: dSpeed }]);
    tpl.querySelector('#configurator-memory').innerHTML = cards;
    return { dimm: dimm, step: step };
  }

  function buildRaid(tpl, tree) {
    var g = group(tree, 'raid') || { partitions: [] };
    var h = '', any = false;
    (g.partitions || []).forEach(function (p) {
      if (!(p.options || []).length) return;
      any = true;
      h += '<div class="configurator__subsection"><span class="configurator__label">' + esc(p.label) + '</span>' +
        '<ul class="configurator__grid" data-raid-type="' + esc(p.label) + '">';
      p.options.forEach(function (o) {
        h += radioCard({ name: 'raid-selection', value: o.sku, checked: !!o.default, qsku: o.sku, unitCents: o.price, qty: 1, consumption: o.consumption_watts, title: o.title || o.sku });
      });
      h += '</ul></div>';
      if (p.key === 'sw') h += infoNotice('Software RAID controllers can only support SATA drives');
    });
    if (!any) h += infoNotice('No RAID controller options for this chassis');
    tpl.querySelector('[data-q-slot="raid-body"]').outerHTML = h;
  }

  function buildDrives(tpl, tree, pageMax) {
    var g = group(tree, 'drives') || { groups: [], recommended: [] };
    var max = g.bay_limit == null ? num(pageMax) : num(g.bay_limit);
    var h = '';
    var rec = g.recommended || [];
    if (rec.length) {
      h += '<div class="configurator__subsection"><span class="configurator__label">Recommended Drives</span><ul class="configurator__grid q-drive-group">';
      rec.forEach(function (o) { h += driveCard(o, max, 'Recommended Drives'); });
      h += '</ul></div>';
    }
    var groups = (g.groups || []).filter(function (x) { return (x.options || []).length > 0; });
    if (!groups.length) h += '<div class="configurator__error-message" style="color: red;">Drive options are not configured for this chassis yet.</div>';
    groups.forEach(function (x) {
      var ff = x.form_factor || '2.5';
      // Live group labels are the list_name minus ' - Quantify', which
      // carries the form factor: 'New SAS SSDs 2.5"'.
      var label = (x.label || x.code) + ' ' + ff + '"';
      h += '<details class="q-drive-expander" data-group-label="' + esc(label) + '" data-group-gid="' + esc(x.code) + '">' +
        '<summary class="configurator__label q-drive-expander__toggle" style="cursor: pointer; display: block; padding: 0.6rem 0;">Show ' + esc(label) + '</summary>' +
        '<div class="configurator__subsection"><ul class="configurator__grid q-drive-group" data-drive-ff="' + esc(ff) + '">';
      var meta = [x.condition, x.media].filter(Boolean).join(' ') || label;
      x.options.forEach(function (o) { h += driveCard(o, max, meta); });
      h += '</ul></div></details>';
    });
    var et = g.empty_tray;
    if (et && et.sku && et.price != null) {
      h += '<div class="configurator__subsection q-empty-tray" data-empty-tray><span class="configurator__label">Empty Drive Trays</span><ul class="configurator__grid">' +
        '<li class="configurator__card q-empty-tray-card"><label class="configurator__card-label"><div class="configurator__card-content">' +
        '<div class="configurator__card__header-wrapper"><div class="configurator__card-header">' +
        '<h3 class="configurator__card-title">' + esc(et.title || et.sku) + '</h3>' +
        '<p class="configurator__card-price q-price q-empty-tray-price" data-unit-cents="' + num(et.price) + '"></p></div>' +
        '<p class="configurator__card-description q-empty-tray-sub">No drive. For customers installing their own drives.</p></div>' +
        '<div class="configurator__card-actions"><div class="quantity">' +
        '<button class="quantity__button" name="minus" type="button">' + icon('minus') + '</button>' +
        '<input class="quantity__input q-empty-tray-input" type="number" min="0" max="99" value="0"' +
        ' data-empty-tray-qsku="' + esc(et.sku) + '" data-unit-cents="' + num(et.price) + '" data-title="' + esc(et.title || et.sku) + '">' +
        '<button class="quantity__button" name="plus" type="button">' + icon('plus') + '</button>' +
        '</div></div></div></label></li></ul></div>';
    }
    tpl.querySelector('[data-q-slot="drives-body"]').outerHTML = h;
    var sub = tpl.querySelector('[data-q-slot="drives-sub"]');
    if (sub) {
      sub.innerHTML = '\n            Select up to ' + max + ' drives\n            <span class="configurator__slot-counter">(<span class="configurator__slots-used">0</span>/' + max + ' Slots Used)</span>\n          ';
      sub.removeAttribute('data-q-slot');
    }
    var limits = [];
    ((tree.chassis && tree.chassis.drive_group_limits) || g.drive_group_limits || []).forEach(function (l) {
      if (l && l.code && l.max_qty != null) limits.push(l.code + '|' + num(l.max_qty));
    });
    return { max: max, limits: limits };
  }

  function buildRemote(tpl, tree) {
    var g = group(tree, 'remote_management') || { options: [] };
    var cards = '';
    (g.options || []).forEach(function (o) {
      cards += radioCard({ name: 'remote-selection', value: o.sku, checked: !!o.default, qsku: o.sku, unitCents: o.price, qty: 1, title: o.title || o.sku });
    });
    tpl.querySelector('#configurator-remotes').innerHTML = cards;
  }

  function buildPsu(tpl, tree) {
    var g = group(tree, 'power_supplies') || { options: [], qty: 1 };
    var qty = num(g.qty, 1) || 1;
    var cards = '';
    (g.options || []).forEach(function (o) {
      cards += radioCard({
        name: 'power-supply-selection', value: o.sku, checked: !!o.default, qsku: o.sku,
        unitCents: o.price, qty: qty, maxWatts: o.max_watts == null ? 0 : o.max_watts,
        title: qty + 'x ' + (o.title || o.sku)
      });
    });
    tpl.querySelector('#configurator-power-supplies').innerHTML = cards;
    var sub = tpl.querySelector('[data-q-slot="psu-sub"]');
    if (sub) { sub.textContent = qty + 'x included in build'; sub.removeAttribute('data-q-slot'); }
    return qty;
  }

  function buildNetwork(tpl, tree) {
    var g = group(tree, 'network') || { options: [] };
    // The synthetic None card is checked exactly when no option is the
    // default (network.none.default in the JSON says the same thing).
    var noneChecked = !defaultOf(g.options);
    var cards = radioCard({ name: 'network-card-selection', value: 'none', checked: noneChecked, qsku: '', unitCents: 0, qty: 0, title: 'None' });
    (g.options || []).forEach(function (o) {
      cards += radioCard({ name: 'network-card-selection', value: o.sku, checked: !!o.default, qsku: o.sku, unitCents: o.price, qty: 1, title: o.title || o.sku });
    });
    tpl.querySelector('#configurator-network-cards').innerHTML = cards;
  }

  function buildOs(tpl, tree) {
    var g = group(tree, 'operating_systems') || { partitions: [] };
    var parts = (g.partitions || []).filter(function (p) { return (p.options || []).length > 0; });
    var h = '';
    if (!parts.length) h += '<div class="configurator__error-message" style="color: red;">Operating system options are not configured for this chassis yet.</div>';
    var fams = [], dFam = null;
    parts.forEach(function (p) {
      fams.push(p.family || p.label || p.key);
      if (defaultOf(p.options)) dFam = p.family || p.label || p.key;
    });
    var visible = dFam != null ? dFam : (parts.length ? (parts[0].family || parts[0].label || parts[0].key) : null);
    // The Liquid filter snippet activates a default only for 'serie' and
    // 'speed' fields, so the OS filter row renders with no active button.
    h += filterHtml([{ field: 'filter_operation_system', label: 'Operating System', values: uniq(fams), active: null }]);
    parts.forEach(function (p) {
      var fam = p.family || p.label || p.key;
      var fh = handleize(fam);
      h += '<div class="configurator__dropdown-wrapper" data-os-type="' + esc(fh) + '"' + (fam === visible ? '' : ' style="display: none;"') + '>' +
        '<select class="configurator__dropdown" name="os-selection" data-os-type="' + esc(fh) + '">' +
        '<option value="">Select ' + esc(fam) + '</option>';
      p.options.forEach(function (o) {
        h += '<option value="' + esc(o.sku) + '" data-qsku="' + esc(o.sku) + '" data-unit-cents="' + num(o.price) + '"' + (o.default ? ' selected' : '') + '>' + esc(o.title || o.sku) + '</option>';
      });
      h += '</select></div>';
    });
    tpl.querySelector('[data-q-slot="os-body"]').outerHTML = h;
  }

  function buildAddons(tpl, tree) {
    var g = group(tree, 'add_ons') || { partitions: [] };
    var parts = g.partitions || [];
    var h = '';
    if (!parts.length) h += infoNotice('No add-on options for this chassis');
    parts.forEach(function (p, i) {
      var name = 'addon-' + (i + 1);
      var label = p.label || p.list_label || p.key;
      h += '<div class="configurator__subsection configurator__addon-subsection" data-addon-list="' + esc(p.key) + '" data-addon-rank="' + (i + 1) + '">' +
        '<span class="configurator__label">' + esc(label) + '</span>' +
        '<ul class="configurator__grid q-addon-grid" data-addon-group="' + esc(name) + '" data-addon-label="' + esc(label) + '">';
      h += radioCard({ name: name, value: 'none', checked: true, qsku: '', unitCents: 0, qty: 0, title: 'None' });
      (p.options || []).forEach(function (o) {
        h += radioCard({ name: name, value: o.sku, checked: false, qsku: o.sku, unitCents: o.price, qty: 1, title: o.title || o.sku, inputAttrs: ' data-addon-cat="' + esc(label) + '"' });
      });
      h += '</ul></div>';
    });
    tpl.querySelector('[data-q-slot="addons-body"]').outerHTML = h;
  }

  // ---------- fill the inert shell, then mount ----------

  function fill(tpl, tree, mount) {
    var root = tpl.querySelector(TAG);
    if (!root) throw new Error('shell root missing');
    var chassis = tree.chassis || {};
    var sockets = num(chassis.sockets, 1) || 1;
    var procQty = buildProcessors(tpl, tree);
    var hsQty = buildHeatsinks(tpl, tree);
    var mem = buildMemory(tpl, tree, sockets);
    buildRaid(tpl, tree);
    var drives = buildDrives(tpl, tree, mount.dataset.qPageMaxDrives);
    buildRemote(tpl, tree);
    var psuQty = buildPsu(tpl, tree);
    buildNetwork(tpl, tree);
    buildOs(tpl, tree);
    buildAddons(tpl, tree);

    if (chassis.base_price != null) root.setAttribute('data-chassis-cents', String(num(chassis.base_price)));
    root.setAttribute('data-processor-qty', String(procQty));
    root.setAttribute('data-psu-qty', String(psuQty));
    root.setAttribute('data-heatsink-qty', String(hsQty));
    root.setAttribute('data-dimm-slots', String(mem.dimm));
    root.setAttribute('data-memory-step', String(mem.step));
    root.setAttribute('data-max-drives', String(drives.max));
    root.setAttribute('data-drive-group-limits', JSON.stringify(drives.limits));
    var bcw = chassis.base_consumption_watts;
    root.setAttribute('data-base-consumption', bcw == null ? String(num(mount.dataset.qPageConsumption)) : String(num(bcw)));
    root.setAttribute('data-engine-generated-at', String((tree.meta && tree.meta.generated_at) || ''));
    root.setAttribute('data-engine-price-basis', String((tree.meta && tree.meta.price_basis_at) || ''));
  }

  // ---------- config string prefill (R-HW-14, G1 section 5 rules) ----------

  function parseConfig(str) {
    var out = { segs: [], tier: null };
    if (!str || str.indexOf('|') === -1) return out;
    str.split('|').forEach(function (seg) {
      seg = seg.trim();
      if (!seg) return;
      if (seg.indexOf('W:') === 0) { out.tier = seg.slice(2).trim(); return; }
      var m = seg.match(/^(.+?)x(\d+)$/);
      if (m) out.segs.push({ sku: m[1], qty: parseInt(m[2], 10) || 0 });
    });
    return out;
  }

  function prefill(el, tree, config) {
    var parsed = parseConfig(config);
    if (!parsed.segs.length && !parsed.tier) return;
    var g = group(tree, 'drives') || {};
    var trays = g.trays || {};
    var nativeTray = g.empty_tray ? g.empty_tray.sku : null;
    var radioNames = ['processor-selection', 'heatsink-selection', 'raid-selection', 'remote-selection', 'power-supply-selection', 'network-card-selection'];
    var addonNames = typeof el.addonGroupNames === 'function' ? el.addonGroupNames() : [];
    var driveSegs = [], traySegs = {}, changed = false;
    parsed.segs.forEach(function (s) {
      if (!s.sku || s.qty < 1) return;
      if (s.sku === el.dataset.chassisQsku) return;
      // Memory: the segment qty is the DIMM count (value "{sku}-{count}").
      if (el.querySelector('input[name="memory-selection"][value="' + s.sku + '-' + s.qty + '"]')) {
        el.setRadioValue('memory-selection', s.sku + '-' + s.qty); changed = true; return;
      }
      var hit = false;
      radioNames.concat(addonNames).forEach(function (name) {
        if (hit) return;
        if (el.querySelector('input[name="' + name + '"][value="' + s.sku + '"]')) { el.setRadioValue(name, s.sku); hit = true; changed = true; }
      });
      if (hit) return;
      var osHit = false;
      el.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach(function (select) {
        if (osHit) return;
        var opt = Array.prototype.find.call(select.options, function (o) { return o.value === s.sku; });
        if (opt) {
          el.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach(function (other) { if (other !== select) other.selectedIndex = 0; });
          select.value = s.sku; osHit = true; changed = true;
          el.querySelectorAll('.configurator__dropdown-wrapper[data-os-type]').forEach(function (w) { w.style.display = w.dataset.osType === select.dataset.osType ? '' : 'none'; });
        }
      });
      if (osHit) return;
      if (el.querySelector('.q-drive-group .quantity__input[data-drive-qsku="' + s.sku + '"]')) { driveSegs.push(s); return; }
      if (nativeTray && s.sku === nativeTray) { traySegs[s.sku] = (traySegs[s.sku] || 0) + s.qty; return; }
      Object.keys(trays).forEach(function (ff) {
        (trays[ff] || []).forEach(function (t) { if (t.tray_sku === s.sku) traySegs[s.sku] = (traySegs[s.sku] || 0) + s.qty; });
      });
    });
    // Drives through applyDriveQty (bay and group clamps apply).
    var expectedNative = 0;
    driveSegs.forEach(function (s) {
      var input = el.querySelector('.q-drive-group .quantity__input[data-drive-qsku="' + s.sku + '"]');
      if (!input) return;
      input.value = s.qty;
      el.applyDriveQty(input);
      changed = true;
      var ff = input.dataset.driveFf;
      (trays[ff] || []).forEach(function (t) { if (t.tray_sku === nativeTray) expectedNative += num(t.qty_per_drive) * (el.selectedDrives.get(s.sku) ? el.selectedDrives.get(s.sku).qty : 0); });
    });
    // Empties = native tray segment overflow past the drives' own trays (Q-HW-1).
    if (nativeTray && traySegs[nativeTray] != null) {
      var empties = Math.max(0, traySegs[nativeTray] - expectedNative);
      var etInput = typeof el.emptyTrayInput === 'function' ? el.emptyTrayInput() : null;
      if (etInput && empties > 0) { etInput.value = empties; el.applyEmptyTrayQty(etInput); changed = true; }
    }
    if (parsed.tier) {
      var years = el.querySelector('#q-warranty-years');
      if (parsed.tier === '180 days Warranty') {
        el.setRadioValue('warranty-selection', 'limited-warranty'); changed = true;
      } else if (years && Array.prototype.some.call(years.options, function (o) { return o.value === parsed.tier; })) {
        years.value = parsed.tier;
        el.setRadioValue('warranty-selection', 'sms-warranty'); changed = true;
      }
    }
    if (changed) {
      el.clearValidationState();
      el.setActiveConfigButton(null);
      el.recalc();
    }
  }

  // ---------- failure ----------

  function unavailable(mount, reason) {
    var tpl = document.getElementById('q-engine-shell');
    if (tpl) tpl.remove();
    var msg = document.createElement('div');
    msg.className = 'configurator__error-message';
    msg.style.cssText = 'color: red; padding: 2rem;';
    msg.textContent = 'Configurator unavailable: ' + (reason || 'the configuration service did not respond') + '. Please refresh the page or contact support.';
    if (mount && mount.isConnected) {
      mount.replaceWith(msg);
    } else {
      var el = document.querySelector(TAG);
      if (el) el.insertAdjacentElement('beforebegin', msg);
    }
    console.error('Quantify engine data layer failed:', reason);
  }

  // ---------- main ----------

  function run() {
    var mount = document.querySelector('.q-engine-mount[data-q-engine]');
    var tpl = document.getElementById('q-engine-shell');
    if (!mount || !tpl) return;
    var base = mount.dataset.qEndpoint;
    var store = mount.dataset.qStore || 'whl';
    var key = mount.dataset.qChassisKey;
    if (!base || !key) { unavailable(mount, 'missing endpoint or chassis key'); return; }
    var config = null;
    try { config = new URLSearchParams(window.location.search).get('config'); } catch (e) { config = null; }
    var url = base + '?store_code=' + encodeURIComponent(store) + '&chassis_key=' + encodeURIComponent(key) + (config ? '&config=' + encodeURIComponent(config) : '');
    var tree = null;
    fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (json) {
        if (!json || json.ok !== true || !Array.isArray(json.groups) || !json.chassis) throw new Error('malformed response');
        tree = json;
        return (window.customElements && customElements.whenDefined) ? customElements.whenDefined(TAG) : null;
      })
      .then(function () {
        var copySrc = mount.dataset.qCopyconfigSrc || null;
        fill(tpl.content, tree, mount);
        // Serialize the filled, inert shell and let the parser create the
        // custom element: the upgrade (constructor, init) runs after the
        // whole subtree is in the document.
        var html = tpl.innerHTML;
        tpl.remove();
        mount.insertAdjacentHTML('beforebegin', html);
        mount.remove();
        var el = document.querySelector(TAG);
        if (!el || typeof el.recalc !== 'function') throw new Error('configurator element did not initialize');
        try { prefill(el, tree, (tree.meta && tree.meta.config) || config); } catch (e) { console.warn('Quantify config prefill skipped:', e); }
        if (copySrc && !document.querySelector('script[src="' + copySrc + '"]')) {
          var s = document.createElement('script');
          s.src = copySrc;
          document.head.appendChild(s);
        }
      })
      .catch(function (err) {
        unavailable(document.querySelector('.q-engine-mount[data-q-engine]') || mount, String(err && err.message ? err.message : err));
      });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
